import { useCallback } from "react";
import type { Category, EventId, PlanEvent } from "../../../../shared/types";
import type { EventFullData, UpdateEventData } from "../../../events/types/event-form-types";
import type { FormAnchor } from "../../hooks/use-week-calendar-state";
import { AddEventForm } from "../../../events/components/event-form/AddEventForm";
import { DraggableEventPanel } from "./DraggableEventPanel";

interface AddEventOverlayProps {
  readonly showAddForm: boolean;
  readonly showFullEditForm: boolean;
  readonly selectedDate: string | null;
  readonly formStartTime: string | null;
  readonly formEndTime: string | null;
  readonly formAnchor?: FormAnchor | null;
  readonly editingEvent: PlanEvent | null;
  readonly selectedEvents: readonly PlanEvent[];
  readonly activeCategory: Category;
  readonly onAddEvent: (
    title: string,
    category: Category,
    date: string,
    startTime: string,
    endTime: string,
  ) => void;
  readonly onAddEventFull?: (data: EventFullData) => void;
  readonly onUpdateEvent?: (id: EventId, updates: UpdateEventData) => void;
  readonly onCloseAddForm: () => void;
  readonly onCloseEditForm: () => void;
}

export function AddEventOverlay({
  showAddForm,
  showFullEditForm,
  selectedDate,
  formStartTime,
  formEndTime,
  formAnchor,
  editingEvent,
  selectedEvents,
  activeCategory,
  onAddEvent,
  onAddEventFull,
  onUpdateEvent,
  onCloseAddForm,
  onCloseEditForm,
}: AddEventOverlayProps) {
  const handleCloseAddForm = useCallback(() => {
    onCloseAddForm();
  }, [onCloseAddForm]);

  const handleCloseEditForm = useCallback(() => {
    onCloseEditForm();
  }, [onCloseEditForm]);

  if (!selectedDate) return null;

  return (
    <>
      {/* Formulaire de création d'événement */}
      <DraggableEventPanel
        show={showAddForm}
        onClose={handleCloseAddForm}
        positionOptions={{ formAnchor }}
      >
        <AddEventForm
          defaultCategory={activeCategory}
          defaultDate={selectedDate}
          defaultStartTime={formStartTime ?? undefined}
          defaultEndTime={formEndTime ?? undefined}
          existingEvents={[...selectedEvents]}
          onAdd={onAddEvent}
          onAddEvent={onAddEventFull}
          onClose={handleCloseAddForm}
        />
      </DraggableEventPanel>

      {/* Formulaire d'édition d'événement */}
      {showFullEditForm && editingEvent && (
        <DraggableEventPanel
          show={showFullEditForm}
          onClose={handleCloseEditForm}
          positionOptions={{ formAnchor }}
        >
          <AddEventForm
            defaultCategory={activeCategory}
            eventToEdit={editingEvent}
            existingEvents={[...selectedEvents]}
            onAdd={onAddEvent}
            onUpdateEvent={(id, updates) => {
              onUpdateEvent?.(id, updates);
            }}
            onClose={handleCloseEditForm}
          />
        </DraggableEventPanel>
      )}
    </>
  );
}
